const DEFAULT_GOOGLE_MAPS_MAP_ID = "DEMO_MAP_ID";

export function getGoogleMapsMapId() {
  return process.env.VUE_APP_GOOGLE_MAPS_MAP_ID || DEFAULT_GOOGLE_MAPS_MAP_ID;
}

export function withGoogleMapsMapId(options = {}) {
  return {
    ...options,
    mapId: options.mapId || getGoogleMapsMapId(),
  };
}

export function createGoogleMapsMarker(options = {}) {
  const maps = window.google && window.google.maps;
  const AdvancedMarkerElement =
    maps && maps.marker && maps.marker.AdvancedMarkerElement;

  if (!AdvancedMarkerElement) {
    return new maps.Marker(options);
  }

  const { map, position, title, icon, label, animation, ...customOptions } =
    options;
  const state = {
    currentMap: map,
    visible: true,
    icon,
    label,
    animation,
  };

  const marker = new AdvancedMarkerElement({
    map,
    position,
    title,
    content: createMarkerContent(state),
  });
  const nativeSetMap =
    typeof marker.setMap === "function" ? marker.setMap.bind(marker) : null;

  Object.assign(marker, customOptions);
  marker.__advancedMarker = true;

  if (!nativeSetMap) {
    marker.setMap = (nextMap) => {
      state.currentMap = nextMap;
      marker.map = state.visible ? nextMap : null;
    };
  }
  marker.getMap = () => marker.map;
  marker.getPosition = () => marker.position;
  marker.setPosition = (nextPosition) => {
    marker.position = nextPosition;
  };
  marker.setVisible = (visible) => {
    state.visible = !!visible;
    setAdvancedMarkerMap(marker, nativeSetMap, state.currentMap, state.visible);
  };
  marker.getVisible = () => state.visible;
  marker.setIcon = (nextIcon) => {
    state.icon = nextIcon;
    marker.content = createMarkerContent(state);
  };
  marker.getIcon = () => state.icon;
  marker.setLabel = (nextLabel) => {
    state.label = nextLabel;
    marker.content = createMarkerContent(state);
  };
  marker.getLabel = () => state.label;
  marker.setAnimation = (nextAnimation) => {
    state.animation = nextAnimation;
    marker.content = createMarkerContent(state);
  };
  marker.getAnimation = () => state.animation;
  marker.setOptions = (nextOptions = {}) => {
    if (Object.prototype.hasOwnProperty.call(nextOptions, "map")) {
      state.currentMap = nextOptions.map;
      setAdvancedMarkerMap(
        marker,
        nativeSetMap,
        state.currentMap,
        state.visible
      );
    }
    if (Object.prototype.hasOwnProperty.call(nextOptions, "position")) {
      marker.setPosition(nextOptions.position);
    }
    if (Object.prototype.hasOwnProperty.call(nextOptions, "icon")) {
      marker.setIcon(nextOptions.icon);
    }
    if (Object.prototype.hasOwnProperty.call(nextOptions, "label")) {
      marker.setLabel(nextOptions.label);
    }
    if (Object.prototype.hasOwnProperty.call(nextOptions, "animation")) {
      marker.setAnimation(nextOptions.animation);
    }
  };

  return marker;
}

function setAdvancedMarkerMap(marker, nativeSetMap, nextMap, visible) {
  const resolvedMap = visible ? nextMap : null;

  if (nativeSetMap) {
    nativeSetMap(resolvedMap);
    return;
  }

  marker.map = resolvedMap;
}

export function openGoogleMapsInfoWindow(infoWindow, map, marker) {
  if (marker && marker.__advancedMarker) {
    infoWindow.open({ map, anchor: marker });
    return;
  }

  infoWindow.open(map, marker);
}

function createMarkerContent({ icon, label, animation }) {
  if (!icon && !label) {
    return undefined;
  }

  ensureAdvancedMarkerStyles();

  if (
    icon &&
    Object.prototype.hasOwnProperty.call(icon, "path") &&
    icon.path !== null &&
    icon.path !== undefined
  ) {
    return createSymbolMarkerContent(icon, label, animation);
  }

  if (icon && icon.url) {
    return createImageMarkerContent(icon, label, animation);
  }

  return createLabelMarkerContent(label, animation);
}

function createSymbolMarkerContent(icon, label, animation) {
  const scale = Number(icon.scale || 8);
  const strokeWeight = Number(icon.strokeWeight || 0);
  const size = Math.max(8, scale * 2 + strokeWeight * 2);
  const element = document.createElement("div");
  element.className = advancedMarkerClassName(animation);
  element.style.width = `${size}px`;
  element.style.height = `${size}px`;
  element.style.borderRadius = "50%";
  element.style.boxSizing = "border-box";
  element.style.background = icon.fillColor || "#1A73E8";
  element.style.opacity = String(icon.fillOpacity || 1);
  element.style.border = `${strokeWeight}px solid ${
    icon.strokeColor || "#ffffff"
  }`;
  element.style.display = "flex";
  element.style.alignItems = "center";
  element.style.justifyContent = "center";

  appendMarkerLabel(element, label);

  return element;
}

function createImageMarkerContent(icon, label, animation) {
  const size = extractIconSize(icon);
  const element = document.createElement("div");
  element.className = advancedMarkerClassName(animation);
  element.style.position = "relative";
  element.style.width = `${size.width}px`;
  element.style.height = `${size.height}px`;
  element.style.display = "inline-flex";
  element.style.alignItems = "center";
  element.style.justifyContent = "center";

  const image = document.createElement("img");
  image.src = icon.url;
  image.alt = "";
  image.style.width = "100%";
  image.style.height = "100%";
  image.style.display = "block";

  element.appendChild(image);
  appendMarkerLabel(element, label);

  return element;
}

function createLabelMarkerContent(label, animation) {
  const element = document.createElement("div");
  element.className = advancedMarkerClassName(animation);
  element.style.minWidth = "24px";
  element.style.minHeight = "24px";
  element.style.borderRadius = "12px";
  element.style.background = "#1A73E8";
  element.style.display = "flex";
  element.style.alignItems = "center";
  element.style.justifyContent = "center";
  element.style.padding = "2px 6px";

  appendMarkerLabel(element, label);

  return element;
}

function appendMarkerLabel(element, label) {
  if (!label) {
    return;
  }

  const labelElement = document.createElement("span");
  const labelText = typeof label === "string" ? label : label.text;
  labelElement.textContent = labelText || "";
  labelElement.style.position = "absolute";
  labelElement.style.left = "50%";
  labelElement.style.top = "50%";
  labelElement.style.transform = "translate(-50%, -50%)";
  labelElement.style.color =
    typeof label === "object" && label.color ? label.color : "#ffffff";
  labelElement.style.fontSize =
    typeof label === "object" && label.fontSize ? label.fontSize : "12px";
  labelElement.style.fontWeight = "700";
  labelElement.style.lineHeight = "1";
  labelElement.style.pointerEvents = "none";
  element.appendChild(labelElement);
}

function extractIconSize(icon) {
  const scaledSize = icon.scaledSize || icon.size || {};
  return {
    width: Number(scaledSize.width || 32),
    height: Number(scaledSize.height || 32),
  };
}

function advancedMarkerClassName(animation) {
  return animation ? "cajupe-advanced-marker-bounce" : "";
}

function ensureAdvancedMarkerStyles() {
  if (document.getElementById("cajupe-advanced-marker-styles")) {
    return;
  }

  const style = document.createElement("style");
  style.id = "cajupe-advanced-marker-styles";
  style.textContent = `
@keyframes cajupeAdvancedMarkerBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
}
.cajupe-advanced-marker-bounce {
  animation: cajupeAdvancedMarkerBounce 700ms ease-in-out infinite;
}
`;
  document.head.appendChild(style);
}
